import { MouseEventHandler, useCallback, useRef, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useCrudEditContext,
  useFixedRidesActions,
  useHubRideActionConfirm,
  useModalConfirm,
  useModalConfirmsRide,
  useRideDuplicate,
  useRideEdit,
  useRideNearbyActions,
  useRidesActions,
  useRidesListActions,
  useRidesNavigation,
} from "@ryde/redux";
import {
  ConfirmResult,
  customLoaderAtom,
  DrawerType,
  editFormDataAF,
  errors,
  getRideHubActionContext,
  hookFormContextAF,
  HubRideActionKind,
  IRideDto,
  isPastTaxiSupplierRide,
  isRenderPolylineAllowedAtom,
  isTranslatableItem,
  Log,
  markKeepEditFormDtoOnUnmount,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  RideDeleteActionsResultEnum,
  rideDrawerSignals,
  rideHasPassengersWithBookings,
  rideHasUnsavedStationChangeAtom,
  ridePostSaveTransitionAtom,
  rideSaveInProgressAtom,
  RideStatusEnum,
  translateInfoItem,
  useDrawers,
  useHaveCustomPermissions,
  warnAndToastSimpleResult,
  warnAndToastTranslatableItem,
} from "@ryde/shared";

const POLYLINE_RENDER_DELAY = 500;

export const useRideFooterActions = () => {
  const { t } = useTranslation();
  const {
    idCopyFrom,
    data: { isEdit },
  } = useCrudEditContext<IRideDto>();
  const {
    navigateRidesEdit,
    isRidesRoute,
    isRidesAddRoute,
    isRidesEditRoute,
    isRidesEditOrAddRoute,
    navigateRidesGridOrKanbanOrMap,
    navigateRidesAdd,
  } = useRidesNavigation();
  const { hasOnlyRideDriverAndVehicleUpdatePermission } =
    useHaveCustomPermissions();
  const { fetchRides } = useRidesActions();
  const [, startTransition] = useTransition();
  const [rideDto, setRideDtoGlobal] = useRecoilState<
    Partial<IRideDto> | undefined
  >(editFormDataAF(RideCrudModeKeyTuple));
  const rideId = rideDto?.id;
  const isDuplicate = Boolean(idCopyFrom);
  const { confirmDialogResult } = useModalConfirm();
  const unitTitle = t(`${RideCrudModeKey.uiUnit}.single`);

  const { deleteRide, cancelRide, renewRide } = useRidesActions();
  const { refetchAllNearbyByRideAndCurrent } = useRideNearbyActions();
  const { removeRide } = useRidesListActions();
  const { saveRide, saveRideDriverOrVehicle } = useRideEdit();
  const { processRideDuplicateDto } = useRideDuplicate();
  const { dynamicConvertEditRideConfirmed } = useFixedRidesActions();
  const { closeDrawer, openRideEditFormDrawer, getDrawer } = useDrawers();
  const setIsCustomLoaderShown = useSetRecoilState(customLoaderAtom);
  const isSavingInProgressRef = useRef(false);
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );
  const { confirmRideArchiveAction, confirmRideCancelAction } =
    useModalConfirmsRide();
  const { confirmHubRideAction } = useHubRideActionConfirm();
  const setIsRenderPolylineAllowed = useSetRecoilState(
    isRenderPolylineAllowedAtom,
  );
  const setRideHasUnsavedStationChange = useSetRecoilState(
    rideHasUnsavedStationChangeAtom,
  );
  const setRideSaveInProgress = useSetRecoilState(rideSaveInProgressAtom);
  const setRidePostSaveTransition = useSetRecoilState(
    ridePostSaveTransitionAtom,
  );

  const markRideSaveInProgress = useEvent((inProgress: boolean): void => {
    rideDrawerSignals.isSaveInProgress = inProgress;
    setRideSaveInProgress(inProgress);
  });

  const closeClick = useEvent((): void => {
    if (isRidesEditRoute || isRidesAddRoute) {
      navigateRidesGridOrKanbanOrMap();
    } else {
      const rideEditFormDrawer = getDrawer(DrawerType.RideEditForm);

      if (rideEditFormDrawer) {
        closeDrawer(DrawerType.RideEditForm);
        rideEditFormDrawer?.drawerProps?.onCloseCustomAction?.();
      }
    }
  });

  const saveRideEventHandler = useCallback(
    async ({ sendToSupplier }: { sendToSupplier: boolean }): Promise<void> => {
      try {
        if (!rideDto) {
          Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.INVALID_DATA`));
          return;
        }

        if (isSavingInProgressRef.current) {
          Log.debug("Saving ride is already in progress");
          return;
        }

        const isHubModifyConfirmed = await confirmHubRideAction({
          rides: [rideDto],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubModifyConfirmed) {
          return;
        }

        isSavingInProgressRef.current = true;
        // Mark save in flight so the drawer close guard skips the
        // unsaved-changes prompt during save → close → reopen.
        markRideSaveInProgress(true);
        rideDrawerSignals.isCloseRequestedDuringSave = false;

        const saveRideResult = await saveRide({
          rideDto,
          sendToSupplier,
          isReturnDtoForGrid: false,
        });
        setIsCustomLoaderShown(true);

        if (saveRideResult.success && saveRideResult.data?.id) {
          // Backend may persist the ride successfully but skip the
          // supplier-send (e.g. past-start rejection for taxi-class providers
          // GETT/ISR/Kastel/Yango via PROVIDERS_THAT_ACCEPT_PAST_RIDE_START).
          // In that case it returns SimpleResult with both success: true and
          // error populated — toast the rejection while the save handling
          // proceeds normally.
          if (isTranslatableItem(saveRideResult.error)) {
            warnAndToastTranslatableItem({
              translatableItem: saveRideResult.error,
              tFunction: t,
            });
          }
          const { drawerProps: prevDrawerProps } =
            getDrawer(DrawerType.RideEditForm) || {};
          // If the user closed the drawer while the save was in flight, honor
          // that intent and do not reopen a fresh drawer for the saved ride.
          const isCloseRequested = rideDrawerSignals.isCloseRequestedDuringSave;

          // Stations (and the rest) are persisted — clear so post-save transition
          // can end on !isDirty alone and not stick forever.
          setRideHasUnsavedStationChange(false);

          // IMPORTANT HERE IS ORDERING: processApiResponse already called
          // cleanupRideFormStateAtoms(), which sets ridePostSaveTransition=false.
          // Re-arm it here AFTER that cleanup so the unsaved-changes prompt stays
          // suppressed across save → close → reopen (or in-place reset) until
          // isDirty settles. If this runs before cleanup, or is omitted, the
          // prompt can flash again on slow networks.
          if (!isCloseRequested) {
            setRidePostSaveTransition(true);
          }

          if (isRidesAddRoute) {
            // The add → edit navigation is async; if the user closed the drawer
            // while the save was in flight they have already been routed back to
            // the grid, so skip navigating into the edit route for the new ride.
            if (!isCloseRequested) {
              setIsRenderPolylineAllowed(false);

              navigateRidesEdit({
                id: saveRideResult.data.id,
                skipFormReset: true,
              });
              /* Hack for RYDE-6162. Reproduced only on staging/production.
               * Main issue in race condition in leaflet.textpath.js.
               * Leaflet.textpath.js tries to set text on the path to polyline.
               * But the _path already hasn't been existed at that moment.
               * Recommend adding a patch to leaflet.textpath.js:95:20, wrap it into try/catch
               */
              setTimeout(() => {
                setIsRenderPolylineAllowed(true);
              }, POLYLINE_RENDER_DELAY);
            }
          } else if (isDuplicate && !isRidesEditRoute) {
            setRideDtoGlobal(saveRideResult.data);
            rideFormContext?.reset(saveRideResult.data);

            if (!isCloseRequested) {
              closeDrawer(DrawerType.RideEditForm);
              openRideEditFormDrawer({
                id: saveRideResult.data.id,
                idCopyFrom: undefined,
                onCloseAction: prevDrawerProps?.onCloseCustomAction,
              });
            }
          } else {
            setRideDtoGlobal(saveRideResult.data);
            rideFormContext?.reset(saveRideResult.data);

            if (!isRidesEditOrAddRoute && !isCloseRequested) {
              closeDrawer(DrawerType.RideEditForm);
              openRideEditFormDrawer({
                id: saveRideResult.data.id,
                idCopyFrom: undefined,
                onCloseAction: prevDrawerProps?.onCloseCustomAction,
              });
            }
          }

          startTransition(() => {
            if (saveRideResult.data?.id) {
              refetchAllNearbyByRideAndCurrent(saveRideResult.data.id);
            }
          });
        } else if (isTranslatableItem(saveRideResult.error)) {
          warnAndToastSimpleResult(saveRideResult, t);
          if (
            saveRideResult.error.translationKey ===
              errors.ride.driverRestWindow &&
            saveRideResult.data
          ) {
            const originalRideDto = saveRideResult.data as Partial<IRideDto>;
            setRideDtoGlobal(originalRideDto);
            rideFormContext?.reset(originalRideDto);
          }
        } else {
          Log.warnAndToast(
            t(`general.RIDES_ACTIONS_WARNINGS.SAVING_ERROR`),
            saveRideResult.error,
          );
        }
      } catch (e) {
        if (isTranslatableItem(e)) {
          Log.warnAndToast(translateInfoItem(e, t));
        } else {
          Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.SAVING_ERROR`), e);
        }
      } finally {
        setIsCustomLoaderShown(false);
        isSavingInProgressRef.current = false;
        markRideSaveInProgress(false);
        rideDrawerSignals.isCloseRequestedDuringSave = false;
      }
    },
    [
      rideDto,
      saveRide,
      confirmHubRideAction,
      setIsCustomLoaderShown,
      setRideHasUnsavedStationChange,
      markRideSaveInProgress,
      setRidePostSaveTransition,
      isRidesAddRoute,
      isDuplicate,
      isRidesEditRoute,
      setIsRenderPolylineAllowed,
      navigateRidesEdit,
      getDrawer,
      closeDrawer,
      setRideDtoGlobal,
      rideFormContext,
      openRideEditFormDrawer,
      isRidesEditOrAddRoute,
      refetchAllNearbyByRideAndCurrent,
      t,
    ],
  );

  const saveRideDriverOrVehicleEventHandler = useEvent(
    async (): Promise<void> => {
      try {
        if (!rideDto) {
          Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.INVALID_DATA`));
          return;
        }

        if (isSavingInProgressRef.current) {
          Log.debug("Saving ride is already in progress");
          return;
        }

        isSavingInProgressRef.current = true;
        // Mark save in flight so the drawer close guard skips the
        // unsaved-changes prompt during save → close → reopen.
        markRideSaveInProgress(true);
        rideDrawerSignals.isCloseRequestedDuringSave = false;

        const saveRideResult = await saveRideDriverOrVehicle({
          rideDto,
        });
        setIsCustomLoaderShown(true);

        if (saveRideResult.success && saveRideResult.data?.id) {
          // Honor a close clicked while the save was in flight.
          const isCloseRequested = rideDrawerSignals.isCloseRequestedDuringSave;
          const { drawerProps: prevDrawerProps } =
            getDrawer(DrawerType.RideEditForm) || {};

          // Driver/vehicle patch persisted — clear station flag so post-save
          // transition is not blocked by a leftover unsaved-stations mark.
          // (processApiResponse already cleans via cleanup; keep explicit for parity
          // with saveRideEventHandler.)
          setRideHasUnsavedStationChange(false);

          // RISK / ORDERING: same as saveRideEventHandler — cleanup inside
          // processApiResponse cleared postSaveTransition; re-arm it here after
          // that cleanup so the post-save dirty window stays suppressed.
          if (!isCloseRequested) {
            setRidePostSaveTransition(true);
          }

          if (isRidesAddRoute) {
            if (!isCloseRequested) {
              navigateRidesEdit({
                id: saveRideResult.data.id,
                skipFormReset: true,
              });
            }
          } else if (isDuplicate && !isRidesEditRoute) {
            setRideDtoGlobal(saveRideResult.data);
            rideFormContext?.reset(saveRideResult.data);

            if (!isCloseRequested) {
              closeDrawer(DrawerType.RideEditForm);
              openRideEditFormDrawer({
                id: saveRideResult.data.id,
                idCopyFrom: undefined,
                onCloseAction: prevDrawerProps?.onCloseCustomAction,
              });
            }
          } else {
            // Same as full save: keep in-place reset on edit/add routes; reopen
            // unbound drawer so post-save dirty window is bridged consistently.
            setRideDtoGlobal(saveRideResult.data);
            rideFormContext?.reset(saveRideResult.data);

            if (!isRidesEditOrAddRoute && !isCloseRequested) {
              closeDrawer(DrawerType.RideEditForm);
              openRideEditFormDrawer({
                id: saveRideResult.data.id,
                idCopyFrom: undefined,
                onCloseAction: prevDrawerProps?.onCloseCustomAction,
              });
            }
          }

          await refetchAllNearbyByRideAndCurrent(saveRideResult.data.id);
        } else if (isTranslatableItem(saveRideResult.error)) {
          warnAndToastSimpleResult(saveRideResult, t);
        } else {
          Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.SAVING_ERROR`));
        }
      } catch (e) {
        if (isTranslatableItem(e)) {
          Log.warnAndToast(translateInfoItem(e, t));
        } else {
          Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.SAVING_ERROR`), e);
        }
      } finally {
        setIsCustomLoaderShown(false);
        isSavingInProgressRef.current = false;
        markRideSaveInProgress(false);
        rideDrawerSignals.isCloseRequestedDuringSave = false;
      }
    },
  );

  const onSaveAndSendToSupplierHandler: MouseEventHandler<HTMLButtonElement> =
    useEvent((e: React.MouseEvent<HTMLButtonElement>): void => {
      e.preventDefault();

      saveRideEventHandler({ sendToSupplier: true });
    });

  const onSaveAndStayClickEvent: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (hasOnlyRideDriverAndVehicleUpdatePermission) {
          saveRideDriverOrVehicleEventHandler();
        } else {
          saveRideEventHandler({ sendToSupplier: false });
        }
      },
      [
        hasOnlyRideDriverAndVehicleUpdatePermission,
        saveRideDriverOrVehicleEventHandler,
        saveRideEventHandler,
      ],
    );

  const onArchiveClickEvent: MouseEventHandler<HTMLButtonElement> = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (!rideId || !isEdit) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.ARCHIVE_INVALID_ID`),
        );
        return;
      }

      // Single-ride drawer: hub paired-ride warning before booking-status archive dialog.
      const isHubArchiveConfirmed = await confirmHubRideAction({
        rides: [rideDto],
        actionKind: HubRideActionKind.Archive,
      });

      if (!isHubArchiveConfirmed) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.ARCHIVE_CANCELED`),
          rideId,
        );
        return;
      }

      const isEverySelectedRideCanceled =
        rideDto.status === RideStatusEnum.Canceled;
      let archiveRideAction: RideDeleteActionsResultEnum =
        RideDeleteActionsResultEnum.CancelBooking;

      const rideHasBookings = rideHasPassengersWithBookings(rideDto);

      if (rideHasBookings) {
        if (isEverySelectedRideCanceled) {
          const confirmationResult = await confirmDialogResult({
            message: t(`general.ARCHIVE_ITEMS_MESSAGE`),
          });

          if (confirmationResult !== ConfirmResult.OK) {
            archiveRideAction = RideDeleteActionsResultEnum.NoAction;
          } else {
            archiveRideAction =
              RideDeleteActionsResultEnum.NotChangeBookingStatus;
          }
        } else {
          archiveRideAction = await confirmRideArchiveAction();
        }
      } else {
        const confirmationResult = await confirmDialogResult({
          message: t(`general.ITEM_ARCHIVE_MESSAGE`),
        });

        if (confirmationResult !== ConfirmResult.OK) {
          archiveRideAction = RideDeleteActionsResultEnum.NoAction;
        }
      }

      if (archiveRideAction === RideDeleteActionsResultEnum.NoAction) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.ARCHIVE_CANCELED`),
          rideId,
        );
        return;
      }

      const deleteResult = await deleteRide(rideId, archiveRideAction);

      if (deleteResult.success && deleteResult.data) {
        removeRide(rideId);
        closeClick();

        const hubContext = getRideHubActionContext(rideDto);
        if (hubContext?.relatedRideIds.length) {
          Log.infoAndToast(
            t("ride.RIDE_DELETED_WITH_RELATED_HUB", {
              relatedRideIds: hubContext.relatedRideIds.join(", "),
            }),
          );
        } else {
          Log.infoAndToast(t("ride.RIDE_DELETED"));
        }
      } else {
        warnAndToastSimpleResult(deleteResult, t);
      }
    },
  );

  const onCancelledClickEvent: MouseEventHandler<HTMLButtonElement> = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (!rideId || !isEdit) {
        Log.warnAndToast(t(errors.ride.invalidRideId));
        return;
      }

      // Single-ride drawer: hub paired-ride warning before booking-status cancel dialog.
      const isHubCancelConfirmed = await confirmHubRideAction({
        rides: [rideDto],
        actionKind: HubRideActionKind.Cancel,
      });

      if (!isHubCancelConfirmed) {
        Log.warnAndToast(t(errors.ride.updatedRideCanceledByUser), rideId);
        return;
      }

      const rideHasBookings = rideHasPassengersWithBookings(rideDto);
      // RYDE-7633: a past taxi-supplier ride is cancelled locally (no provider notification) and
      // requires a free-text reason — always route it through the reason-collecting cancel modal.
      const isPastTaxi = isPastTaxiSupplierRide(rideDto);
      let cancelRideAction = RideDeleteActionsResultEnum.CancelBooking;
      let cancelationReason: string | undefined;

      if (rideHasBookings || isPastTaxi) {
        const cancelResultData = await confirmRideCancelAction({
          requireCancelationReason: isPastTaxi,
        });
        cancelRideAction = cancelResultData.bookingAction;
        cancelationReason = cancelResultData.cancelationReason;
      } else {
        const confirmationResult = await confirmDialogResult({
          message: t(`general.CANCEL_RIDES_MESSAGE`),
        });

        if (confirmationResult !== ConfirmResult.OK) {
          cancelRideAction = RideDeleteActionsResultEnum.NoAction;
        }
      }

      if (cancelRideAction === RideDeleteActionsResultEnum.NoAction) {
        Log.warnAndToast(t(errors.ride.updatedRideCanceledByUser), rideId);
        return;
      }

      const cancelResult = await cancelRide(
        rideId,
        cancelRideAction,
        cancelationReason,
      );

      if (cancelResult.success) {
        if (cancelResult.success && cancelResult.data) {
          setRideDtoGlobal(cancelResult.data);

          if (isRidesRoute) {
            startTransition(() => {
              fetchRides({ backgroundAction: true, isBypassPreloader: true });
            });
          }

          const hubContext = getRideHubActionContext(rideDto);
          if (hubContext?.relatedRideIds.length) {
            Log.infoAndToast(
              t("ride.RIDE_CANCELED_WITH_RELATED_HUB", {
                relatedRideIds: hubContext.relatedRideIds.join(", "),
              }),
            );
          } else {
            Log.infoAndToast(t("ride.RIDE_CANCELED"));
          }
        }
      } else {
        warnAndToastSimpleResult(cancelResult, t);
      }
    },
  );

  const onRenewRidesClickEvent: MouseEventHandler<HTMLButtonElement> = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (!rideId || !isEdit) {
        Log.warnAndToast(t(errors.ride.invalidRideId));
        return;
      }

      const confirmationResult = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t(`general.RENEW_RIDE_MESSAGE`),
      });

      if (confirmationResult === ConfirmResult.OK) {
        // Renew mutates paired hub rides — treated as Modify, not a status-only action.
        const isHubRenewConfirmed = await confirmHubRideAction({
          rides: rideDto ? [rideDto] : [],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubRenewConfirmed) {
          return;
        }

        const renewResult = await renewRide(rideId);

        if (renewResult.success) {
          if (renewResult.success && renewResult.data) {
            setRideDtoGlobal(renewResult.data);

            if (isRidesRoute) {
              startTransition(() => {
                fetchRides({ backgroundAction: true, isBypassPreloader: true });
              });
            }

            Log.infoAndToast(t("ride.RIDE_RENEWED"));
          }
        } else {
          warnAndToastSimpleResult(renewResult, t);
        }
      }
    },
  );

  const duplicateClickHandler = useEvent(
    async ({
      reverseStations,
      forNewSupplier = false,
    }: {
      reverseStations: boolean;
      forNewSupplier?: boolean;
    }) => {
      if (!rideId || isRidesAddRoute) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.DUPLICATION_INVALID_ID`),
        );
        return;
      }

      const duplicationActionConfirmed = await confirmDialogResult(
        forNewSupplier
          ? {
              title: t("ride.DUPLICATE_TO_NEW_SUPPLIER_CONFIRM_TITLE"),
              message: t("ride.DUPLICATE_TO_NEW_SUPPLIER_CONFIRM"),
            }
          : {
              title: reverseStations
                ? t("ride.CREATE_OPPOSITE_DIRECTION_RIDE_CONFIRM_TITLE")
                : t("general.DUPLICATE_CONFIRM"),
              message: reverseStations
                ? t("ride.CREATE_OPPOSITE_DIRECTION_RIDE_CONFIRM")
                : t(`general.DUPLICATE_MESSAGE`, { entity: unitTitle }),
            },
      );

      if (duplicationActionConfirmed !== ConfirmResult.OK) {
        Log.infoAndToast(
          reverseStations
            ? t(`ride.CREATE_OPPOSITE_DIRECTION_RIDE_ACTION_CANCELED`)
            : t(`general.RIDES_ACTIONS_WARNINGS.GENERAL_OPERATION_CANCELED`),
        );
        return;
      }

      setIsCustomLoaderShown(true);

      // finally: a failure here must not leave the blocking fullscreen loader on screen
      try {
        const duplicatedDto = processRideDuplicateDto({
          rideDto,
          reverseStations,
          forNewSupplier,
        });

        if (isRidesEditRoute) {
          Log.debug(
            "Duplication for ride edit route, navigation to add",
            duplicatedDto,
          );
          await navigateRidesAdd({
            keepQuery: true,
            initialData: duplicatedDto,
            rideIdCopyFrom: rideId,
            revertDirection: reverseStations,
          });
        } else {
          Log.debug("Duplication in unbound mode");
          markKeepEditFormDtoOnUnmount(RideCrudModeKeyTuple);
          closeDrawer(DrawerType.RideEditForm);

          // Open first: opening a drawer clears editFormDataAF for its key, so the processed
          // duplicate must be written after it (otherwise it is refetched and forNewSupplier is lost).
          void openRideEditFormDrawer({
            id: undefined,
            idCopyFrom: rideDto.id,
            duplicateReverseStations: reverseStations,
          });

          setRideDtoGlobal(duplicatedDto);
          rideFormContext?.reset(duplicatedDto);
        }
      } finally {
        setIsCustomLoaderShown(false);
      }
    },
  );

  const onDuplicateClickEvent: MouseEventHandler<HTMLButtonElement> = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();
      duplicateClickHandler({ reverseStations: false });
    },
  );

  const onDuplicateRevertedRideClick: MouseEventHandler<HTMLButtonElement> =
    useEvent(async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();
      duplicateClickHandler({ reverseStations: true });
    });

  // RYDE-7633: re-dispatch a past taxi ride to a new supplier by duplicating it (old supplier and
  // its external data stripped, rescheduled to now + lead time).
  const onDuplicateToNewSupplierClick: MouseEventHandler<HTMLButtonElement> =
    useEvent(async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();
      duplicateClickHandler({ reverseStations: false, forNewSupplier: true });
    });

  const onConvertToDynamicRideClick: MouseEventHandler<
    HTMLButtonElement
  > = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    const convertResult = await dynamicConvertEditRideConfirmed();

    if (!convertResult.success) {
      warnAndToastSimpleResult(convertResult, t);
    }
  };

  return {
    onArchiveClickEvent,
    onConvertToDynamicRideClick,
    onDuplicateClickEvent,
    onSaveAndStayClickEvent,
    onSaveAndSendToSupplierHandler,
    onCancelledClickEvent,
    onRenewRidesClickEvent,
    onDuplicateRevertedRideClick,
    onDuplicateToNewSupplierClick,
  };
};
